import { motion, AnimatePresence } from "framer-motion";
import { X, ShareNetwork, Plus, BellRinging, House, ArrowDown } from "@phosphor-icons/react";

/**
 * IosInstallSheet — visual walkthrough for iPhone customers.
 *
 * Apple/Safari only allows web push if our site is added to the home
 * screen as a PWA. This sheet teaches that with three big visual steps
 * + a friendly explanation. Shown either:
 *   - When the soft push prompt fires on iOS-in-Safari (instead of the
 *     plain text hint), or
 *   - When a customer taps "Notify me when live" on iOS-in-Safari.
 */
export default function IosInstallSheet({ open, onClose, truckName = null }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
          onClick={onClose}
          data-testid="ios-install-sheet"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md rounded-2xl border border-border bg-card shadow-2xl overflow-hidden"
            role="dialog"
            aria-labelledby="ios-install-title"
          >
            <div className="relative p-5 pb-4 bg-gradient-to-b from-primary/10 to-transparent">
              <button
                type="button"
                onClick={onClose}
                className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition"
                aria-label="Close"
                data-testid="ios-install-close"
              >
                <X weight="bold" className="h-4 w-4" />
              </button>
              <div className="h-12 w-12 grid place-items-center rounded-2xl bg-primary text-primary-foreground shadow-lg">
                <BellRinging weight="duotone" className="h-7 w-7" />
              </div>
              <h2 id="ios-install-title" className="mt-3 font-display font-bold text-xl leading-tight" data-testid="ios-install-title">
                {truckName
                  ? `Get a ping when ${truckName} is live`
                  : "Get pinged when your favorite trucks are live"}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground leading-snug">
                On iPhone, you'll need to add TruckTrackr to your Home Screen first. Takes 10 seconds.
              </p>
            </div>

            <ol className="px-5 pb-3 space-y-3">
              <Step
                n={1}
                title={(
                  <>Tap the <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                    <ShareNetwork weight="duotone" className="h-4 w-4 text-primary" /> Share
                  </span> button</>
                )}
                hint="It's at the bottom of Safari (or top on iPad)."
                icon={<ShareNetwork weight="duotone" className="h-5 w-5 text-primary" />}
              />
              <Step
                n={2}
                title={(
                  <>Scroll, then tap <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                    <Plus weight="bold" className="h-3.5 w-3.5" /> Add to Home Screen
                  </span></>
                )}
                hint="It's a few rows down in the share sheet."
                icon={<ArrowDown weight="duotone" className="h-5 w-5 text-primary" />}
              />
              <Step
                n={3}
                title={(
                  <>Open TruckTrackr from your <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                    <House weight="duotone" className="h-4 w-4 text-primary" /> Home Screen
                  </span></>
                )}
                hint={truckName
                  ? `Then tap the bell on ${truckName} again — you'll get a ping the moment they're live.`
                  : "Then come back and tap any bell — we'll only ping you for trucks you pick."}
                icon={<BellRinging weight="duotone" className="h-5 w-5 text-primary" />}
              />
            </ol>

            <div className="px-5 pb-5 pt-2 border-t border-border/60 bg-muted/30">
              <button
                type="button"
                onClick={onClose}
                className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition"
                data-testid="ios-install-got-it"
              >
                Got it
              </button>
              <p className="mt-2 text-[11px] text-muted-foreground text-center leading-snug">
                Quiet by default — no spam, just a heads-up when trucks you care about are live nearby.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Step({ n, title, hint, icon }) {
  return (
    <li className="flex gap-3 items-start">
      <div className="flex-none h-9 w-9 rounded-full bg-primary/10 grid place-items-center font-bold text-primary text-sm">
        {n}
      </div>
      <div className="min-w-0 flex-1 pt-1">
        <div className="text-sm leading-snug text-muted-foreground">{title}</div>
        {hint && (
          <div className="mt-0.5 text-xs text-muted-foreground/80 leading-snug">{hint}</div>
        )}
      </div>
      <div className="flex-none h-9 w-9 rounded-xl bg-muted grid place-items-center">
        {icon}
      </div>
    </li>
  );
}
